/**
 * The Files tab (design "Flow Project Redesign", Files board): every file of the project's Brain in one sortable table,
 * with filters (Inputs, Results, Brain), a search, and Download. One look at `brain/` (three levels) is how the Home
 * reads it too. Files dropped anywhere on this panel (or picked with Add files) become Inputs; each one says whether it
 * made it. A list that cannot be read says so, with Try again, instead of looking empty. Rows can be selected for
 * Download, Move to Brain and Delete (both ask first, naming every file). A file's name (or Enter on its row) opens it
 * here, in the tab (FileView, `focus: { path }`, `?tab=files&file=…`, the title "<Project> / Files / <path>"): these
 * files are not code. A file of a service (`services/<name>/…`) opens in Code, as the chat's file links do
 * (`?view=files&path=…`); so does every file without `onOpenView` (the tab's view to move to). Preview shows a file in a
 * pane beside the table.
 * There is no "Used by" column: nothing this tab can read says which chat or agent turn used a file.
 * Below, the project's context repos (ContextRepos.jsx): git repositories added only for the agent and search to read,
 * with "Add repo as context…".
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'antd';
import { DownloadSimpleIcon, TrashIcon } from '@phosphor-icons/react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as vfsClient from '../../utils/vfsClient';
import {
  FILE_KINDS, SCREENSHOTS_DIR, agentReads, brainMoves, fileRows, kindCounts, shownRows,
} from '../../utils/filesModel';
import { ageOf, formatBytes } from '../home/FilesSection';
import { uniqueName } from '../../utils/projectOverview';
import { cleanFilePath, serviceOfFile } from '../../utils/filesPath';
import { isOsFileDrag } from '../../utils/treeUpload/collectEntries';
import FilePreview from './FilePreview';
import FileView from './FileView';
import ContextRepos from './ContextRepos';
import './FilesPanel.css';
import DataTable from '../table/DataTable';
import { IconButton } from '../table/TableParts';
import { searchForHomeMode } from '../../utils/projectTabUrl';

const KIND_LABEL = { input: 'Input', result: 'Result', brain: 'Brain', screenshot: 'Screenshot' };

function download(projectId, row, onError) {
  vfsClient.readBinaryFile(projectId, row.path).then((blob) => {
    if (!blob) { throw new Error('The file could not be read'); }
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = row.name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }).catch((error) => onError(error?.message || 'The file could not be downloaded'));
}

const INPUTS_DIR = 'brain/inputs/original';
const files = (count) => `${count} file${count === 1 ? '' : 's'}`;

/** The question before Move to Brain or Delete, naming every file (and, for a move, where it goes and what is skipped). */
function ConfirmFiles({ pending, onCancel, onConfirm }) {
  if (!pending) { return null; }
  const remove = pending.action === 'delete';
  const count = remove ? pending.rows.length : pending.moves.length;
  return (
    <Modal
      cancelText={count ? 'Cancel' : 'Close'}
      okButtonProps={{ danger: remove, style: count ? undefined : { display: 'none' } }}
      okText={remove ? `Delete ${files(count)}` : `Move ${files(count)}`}
      onCancel={onCancel}
      onOk={onConfirm}
      open
      title={remove ? `Delete ${files(count)}?` : (count ? `Move ${files(count)} to the Brain?` : 'Nothing to move')}
    >
      {remove && (
        <>
          <p>These files are removed from the project:</p>
          <ul aria-label="Files to delete">{pending.rows.map((row) => <li key={row.path}><strong>{row.name}</strong> <span className="files-dir">{row.dir}</span></li>)}</ul>
        </>
      )}
      {!remove && count > 0 && (
        <>
          <p>The agent reads the Brain before it works. These files move there:</p>
          <ul aria-label="Files to move">{pending.moves.map((move) => <li key={move.from}><strong>{move.name}</strong> to {move.to}</li>)}</ul>
        </>
      )}
      {!remove && pending.skipped.length > 0 && (
        <>
          <p>Skipped:</p>
          <ul aria-label="Skipped files">{pending.skipped.map((entry) => <li key={entry.name}><strong>{entry.name}</strong>: {entry.reason}</li>)}</ul>
        </>
      )}
    </Modal>
  );
}

ConfirmFiles.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  pending: PropTypes.shape({ action: PropTypes.string, moves: PropTypes.array, rows: PropTypes.array, skipped: PropTypes.array }),
};

/** Opens a project file in Code the way a chat file link does: `?tab=code&path=…` on this page (Canvas follows it). */
function useOpenInCode() {
  const navigate = useNavigate();
  const location = useLocation();
  return (path) => {
    const params = new URLSearchParams(searchForHomeMode(location.search, 'files'));
    params.delete('line');
    params.set('path', path);
    navigate({ pathname: location.pathname, search: `?${params.toString()}`, hash: location.hash });
  };
}

/** A row for a file the table does not list (a link to `.flow/…` or the root's README): its name and folder only. */
const rowOf = (path) => {
  const parts = path.split('/');
  return { path, name: parts.pop(), dir: parts.join('/') || '.', kind: null };
};

export default function FilesPanel({ projectId, canEdit = true, onOpenFile = null, focus = null, onOpenView = null, canOpenCode = true }) {
  const openInCode = useOpenInCode();
  // In the tab: any file but a service's (there, Code is where it is read and changed).
  const inTab = (path) => !onOpenFile && Boolean(onOpenView) && !serviceOfFile(path);
  const open = (path) => {
    if (onOpenFile) { onOpenFile(path); return; }
    if (inTab(path)) { onOpenView({ tab: 'files', focus: { path } }); return; }
    openInCode(path);
  };
  const shownFile = cleanFilePath(focus?.path);
  const [rows, setRows] = useState(null);
  const [kind, setKind] = useState('all');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState({ key: 'modified', dir: 'desc' });
  const [error, setError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [uploads, setUploads] = useState(null); // the last upload, one line per file: `{ name, savedAs, ok, error }`
  const [busy, setBusy] = useState(false);
  const picker = useRef(null);
  const dragDepth = useRef(0);
  const [dragging, setDragging] = useState(false);
  const [selected, setSelected] = useState([]); // paths
  const [previewPath, setPreviewPath] = useState(null);
  const [pending, setPending] = useState(null); // `{ action: 'move', moves, skipped }` or `{ action: 'delete', rows }`
  const [report, setReport] = useState(null); // `{ text, failed: [{ name, error }] }`

  const load = useCallback(async() => {
    try {
      const root = await vfsClient.readTree(projectId, '.', 1);
      const has = (name) => (root?.children || []).some((child) => child.name === name && child.type === 'directory');
      const [brain, screenshots] = await Promise.all([
        has('brain') ? vfsClient.readTree(projectId, 'brain', 4) : null,
        has(SCREENSHOTS_DIR) ? vfsClient.readTree(projectId, SCREENSHOTS_DIR, 3) : null,
      ]);
      setRows(fileRows(brain, screenshots));
      setLoadError(null);
    } catch (failure) {
      setLoadError(failure?.message || 'The files could not be read');
      setRows((now) => now || []);
    }
  }, [projectId]);
  useEffect(() => { setRows(null); setLoadError(null); load(); }, [load]);

  // Added files become Inputs, under a free name (never over a file that is there). One failing does not stop the
  // others; each says how it went.
  const addFiles = async(list) => {
    const files = Array.from(list || []);
    if (files.length === 0) { return; }
    setBusy(true);
    setError(null);
    setUploads(null);
    const taken = (rows || []).filter((row) => row.dir === INPUTS_DIR).map((row) => row.name);
    const results = [];
    for (const file of files) {
      const name = uniqueName(file.name, taken);
      taken.push(name);
      try {
        // eslint-disable-next-line no-await-in-loop -- one at a time keeps the names unique
        await vfsClient.writeBinaryFile(projectId, `${INPUTS_DIR}/${name}`, file);
        results.push({ name: file.name, savedAs: name, ok: true });
      } catch (failure) {
        results.push({ name: file.name, savedAs: name, ok: false, error: failure?.message || 'The upload failed' });
      }
    }
    setUploads(results);
    setBusy(false);
    load();
  };

  // The whole panel takes a drop of files from the computer (the copy says so); a drag inside the page is left alone.
  const dropProps = canEdit ? {
    onDragEnter: (event) => {
      if (!isOsFileDrag(event.dataTransfer)) { return; }
      event.preventDefault();
      dragDepth.current += 1;
      setDragging(true);
    },
    onDragOver: (event) => { if (isOsFileDrag(event.dataTransfer)) { event.preventDefault(); } },
    onDragLeave: () => {
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) { setDragging(false); }
    },
    onDrop: (event) => {
      if (!isOsFileDrag(event.dataTransfer)) { return; }
      event.preventDefault();
      event.stopPropagation();
      dragDepth.current = 0;
      setDragging(false);
      if (!busy) { addFiles(event.dataTransfer?.files); }
    },
  } : {};
  const added = (uploads || []).filter((entry) => entry.ok);
  const failed = (uploads || []).filter((entry) => !entry.ok);

  const counts = useMemo(() => kindCounts(rows || []), [rows]);
  const shown = useMemo(() => shownRows(rows || [], { kind, query, ...sort }), [rows, kind, query, sort]);
  const paths = new Set((rows || []).map((row) => row.path));
  const chosen = selected.filter((path) => paths.has(path));
  const chosenRows = (rows || []).filter((row) => chosen.includes(row.path));
  const shownPaths = shown.map((row) => row.path);
  const allShown = shownPaths.length > 0 && shownPaths.every((path) => chosen.includes(path));
  const toggle = (path) => setSelected((now) => (now.includes(path) ? now.filter((x) => x !== path) : [...now, path]));
  const toggleAll = () => setSelected(allShown ? chosen.filter((path) => !shownPaths.includes(path)) : [...new Set([...chosen, ...shownPaths])]);
  const previewRow = (rows || []).find((row) => row.path === previewPath) || null;

  const askMove = () => { setReport(null); setPending({ action: 'move', ...brainMoves(rows || [], chosen, uniqueName) }); };
  // The selected files, or one row's own Delete: asked first either way, naming the file(s).
  const askDelete = (rows = chosenRows) => { setReport(null); setPending({ action: 'delete', rows }); };
  // One file at a time, each with its own result; the ones that fail are named with why.
  const confirm = async() => {
    const { action } = pending;
    const steps = action === 'move'
      ? pending.moves.map((move) => ({ path: move.from, name: move.name, run: () => vfsClient.moveEntry(projectId, move.from, move.to) }))
      : pending.rows.map((row) => ({ path: row.path, name: row.name, run: () => vfsClient.deleteEntry(projectId, row.path) }));
    setPending(null);
    if (steps.length === 0) { return; }
    setBusy(true);
    const done = [];
    const failures = [];
    for (const step of steps) {
      try {
        // eslint-disable-next-line no-await-in-loop -- one at a time, each with its own result
        await step.run();
        done.push(step);
      } catch (failure) {
        failures.push({ name: step.name, error: failure?.message || 'it failed' });
      }
    }
    const verb = action === 'move' ? 'Moved to the Brain' : 'Deleted';
    const count = `${done.length} file${done.length === 1 ? '' : 's'}`;
    setReport({ text: done.length ? `${verb}: ${count} (${done.map((step) => step.name).join(', ')}).` : 'Nothing was changed.', failed: failures });
    setSelected([]);
    if (done.some((step) => step.path === previewPath)) { setPreviewPath(null); }
    setBusy(false);
    load();
  };

  // A new column sorts names and types A→Z, sizes and dates largest and newest first.
  const sortBy = (next) => setSort(next.key === sort.key ? next : { key: next.key, dir: next.key === 'name' || next.key === 'kind' ? 'asc' : 'desc' });

  if (shownFile) {
    return (
      <div className="files-panel" data-testid="files-panel">
        <FileView
          key={shownFile}
          onBack={() => onOpenView?.({ tab: 'files', focus: null })}
          onDownload={(row) => download(projectId, row, setError)}
          onEdit={canEdit && canOpenCode ? openInCode : null}
          onOpenFile={open}
          projectId={projectId}
          row={(rows || []).find((row) => row.path === shownFile) || rowOf(shownFile)}
        />
        {error && <div className="files-error" role="alert">{error}</div>}
      </div>
    );
  }
  if (rows === null) { return <div className="files-panel" data-testid="files-panel"><p className="files-empty">Loading the files…</p></div>; }
  return (
    <div className={`files-panel${dragging ? ' is-over' : ''}`} data-testid="files-panel" {...dropProps}>
      <div className="files-bar">
        <div aria-label="Type" className="files-pills" role="group">
          {FILE_KINDS.map((item) => (
            <button aria-pressed={kind === item.id} className={`files-pill${kind === item.id ? ' is-on' : ''}`} data-testid={`files-kind-${item.id}`} key={item.id} onClick={() => setKind(item.id)} type="button">
              {item.label} {counts[item.id]}
            </button>
          ))}
        </div>
        <input aria-label="Search files" className="files-search" onChange={(event) => setQuery(event.target.value)} placeholder="Search files and folders" type="search" value={query} />
      </div>
      {canEdit && (
        <div className={`files-drop${dragging ? ' is-over' : ''}`} data-testid="files-drop">
          <span>{dragging ? 'Drop to add them as Inputs' : 'Drop files anywhere on this panel'}</span>
          <button className="files-btn" disabled={busy} onClick={() => picker.current?.click()} type="button">{busy ? 'Adding…' : 'Add files'}</button>
          <input data-testid="files-input" hidden multiple onChange={(event) => { addFiles(event.target.files); event.target.value = ''; }} ref={picker} type="file" />
        </div>
      )}
      <p className="files-hint">Added files become Inputs. The Brain is the project&apos;s notes, kept apart from the code; the agent reads it before it works.</p>
      {error && <div className="files-error" role="alert">{error}</div>}
      {uploads && (
        <div className={`files-uploads${failed.length ? ' is-bad' : ''}`} data-testid="files-uploads" role={failed.length ? 'alert' : 'status'}>
          <div className="files-uploads-head">
            <strong>
              {failed.length === 0 && `Added ${added.length} file${added.length === 1 ? '' : 's'} as Inputs.`}
              {failed.length > 0 && added.length > 0 && `Added ${added.length} of ${uploads.length} files; ${failed.length} failed.`}
              {failed.length > 0 && added.length === 0 && `No file was added: ${failed.length === 1 ? 'the upload' : `all ${failed.length} uploads`} failed.`}
            </strong>
            <button aria-label="Dismiss the upload result" className="files-link" onClick={() => setUploads(null)} type="button">Dismiss</button>
          </div>
          <ul className="files-uploads-list">
            {uploads.map((entry, index) => (
              // eslint-disable-next-line react/no-array-index-key -- two files may have the same name
              <li className={entry.ok ? 'is-ok' : 'is-bad'} data-testid={`files-upload-${index}`} key={`${entry.name}-${index}`}>
                {entry.ok ? 'Added' : 'Failed'} <strong>{entry.name}</strong>
                {entry.ok && entry.savedAs !== entry.name && <> as {entry.savedAs} (that name was taken)</>}
                {!entry.ok && <>: {entry.error}</>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {report && (
        <div className={`files-uploads${report.failed.length ? ' is-bad' : ''}`} data-testid="files-report" role={report.failed.length ? 'alert' : 'status'}>
          <div className="files-uploads-head">
            <strong>{report.text}</strong>
            <button aria-label="Dismiss the result" className="files-link" onClick={() => setReport(null)} type="button">Dismiss</button>
          </div>
          {report.failed.length > 0 && (
            <ul className="files-uploads-list">
              {report.failed.map((entry) => <li className="is-bad" key={entry.name}>Failed <strong>{entry.name}</strong>: {entry.error}</li>)}
            </ul>
          )}
        </div>
      )}
      {chosen.length > 0 && (
        <div aria-label="Actions on the selected files" className="files-bulk" data-testid="files-bulk" role="toolbar">
          <strong className="files-grow">{chosen.length} selected</strong>
          <button className="files-btn" disabled={busy} onClick={() => chosenRows.forEach((row) => download(projectId, row, setError))} type="button">Download</button>
          <button className="files-btn" disabled={busy || chosenRows.every((row) => row.kind === 'brain')} onClick={askMove} type="button">Move to Brain</button>
          <button className="files-btn is-danger" disabled={busy} onClick={() => askDelete()} type="button">Delete</button>
        </div>
      )}
      {loadError && (
        <div className="files-error" data-testid="files-load-error" role="alert">
          <span>The files could not be read: {loadError}</span>
          <button className="files-btn" onClick={() => { setLoadError(null); load(); }} type="button">Try again</button>
        </div>
      )}
      {loadError && rows.length === 0 ? null : shown.length === 0 ? (
        <p className="files-empty" data-testid="files-none">{rows.length === 0 ? 'No files yet.' : 'No file matches.'}</p>
      ) : (
        <div className={`files-main${previewRow ? ' has-preview' : ''}`}>
          <div className="files-table-wrap">
            <DataTable
              actions={{
                label: 'Actions',
                width: canEdit ? 200 : 150,
                render: (row) => (
                  <span className="files-row-actions">
                    <button
                      aria-label={`Preview ${row.name}`}
                      aria-pressed={row.path === previewPath}
                      className="files-btn"
                      onClick={() => setPreviewPath(row.path === previewPath ? null : row.path)}
                      type="button"
                    >Preview</button>
                    <IconButton className="files-btn is-icon" icon={<DownloadSimpleIcon aria-hidden size={14} />} label={`Download ${row.name}`} onClick={() => download(projectId, row, setError)} tip="Download" />
                    {canEdit && <IconButton className="files-btn is-danger is-icon" disabled={busy} icon={<TrashIcon aria-hidden size={14} />} label={`Delete ${row.name}`} onClick={() => askDelete([row])} tip="Delete" />}
                  </span>
                ),
              }}
              ariaLabel="Files"
              className="files-table"
              columns={[
                {
                  key: 'name',
                  label: 'Name',
                  sortTestId: 'files-sort-name',
                  value: (row) => row.name,
                  title: (row) => row.path,
                  render: (row) => (
                    <>
                      <button aria-label={`Open ${row.name}${inTab(row.path) ? '' : ' in Code'}`} className="files-name" onClick={() => open(row.path)} type="button">
                        <strong>{row.name}</strong>
                      </button>
                      <div className="files-dir dt-line">
                        {row.dir}
                        {agentReads(row) && <span className="files-agent" title="The agent reads the Brain before it works"> · agent reads it</span>}
                      </div>
                    </>
                  ),
                },
                { key: 'kind', label: 'Type', width: 120, sortTestId: 'files-sort-kind', value: (row) => KIND_LABEL[row.kind], render: (row) => <span className={`files-kind is-${row.kind}`}>{KIND_LABEL[row.kind]}</span> },
                { key: 'size', label: 'Size', width: 100, sortTestId: 'files-sort-size', value: (row) => row.size, render: (row) => formatBytes(row.size) || '—' },
                { key: 'modified', label: 'Added', width: 120, sortTestId: 'files-sort-modified', value: (row) => row.modified, render: (row) => ageOf(row.modified) || '—', title: (row) => (row.modified ? new Date(row.modified).toLocaleString() : undefined) },
              ]}
              leading={canEdit ? {
                width: 40,
                header: <input aria-label="Select all shown files" checked={allShown} onChange={toggleAll} type="checkbox" />,
                render: (row) => <input aria-label={`Select ${row.name}`} checked={chosen.includes(row.path)} onChange={() => toggle(row.path)} type="checkbox" />,
              } : undefined}
              onSort={sortBy}
              rowKey="path"
              rowProps={(row) => ({
                'aria-selected': chosen.includes(row.path),
                className: row.path === previewPath ? 'is-previewed' : undefined,
                'data-testid': `files-row-${row.path}`,
                onKeyDown: (event) => { if (event.key === 'Enter' && event.target === event.currentTarget) { open(row.path); } },
                tabIndex: 0,
              })}
              rows={shown}
              serverSort
              sort={sort}
              storageKey="flow.files.hiddenColumns"
            />
          </div>
          {previewRow && (
            <FilePreview
              key={previewRow.path}
              onClose={() => setPreviewPath(null)}
              onDownload={(row) => download(projectId, row, setError)}
              projectId={projectId}
              row={previewRow}
            />
          )}
        </div>
      )}
      {projectId && <ContextRepos canEdit={canEdit} projectId={projectId} />}
      <ConfirmFiles onCancel={() => setPending(null)} onConfirm={confirm} pending={pending} />
    </div>
  );
}

FilesPanel.propTypes = {
  canEdit: PropTypes.bool,
  // Whether Code is there (the plan may hide it): the file view's Edit in Code.
  canOpenCode: PropTypes.bool,
  // The tab's view: `{ path }` shows that file (FileView) in place of the table.
  focus: PropTypes.shape({ path: PropTypes.string }),
  // Replaces where a file opens (every file).
  onOpenFile: PropTypes.func,
  // (view) => void: the workspace view to move to (`{ tab: 'files', focus: { path } | null }`); none: files open in Code.
  onOpenView: PropTypes.func,
  projectId: PropTypes.string,
};
